import React, { useState, useEffect } from "react";
import {
  FiUsers,
  FiShoppingCart,
  FiCheckSquare,
  FiBarChart,
  FiAward,
  FiLogOut,
  FiMenu,
  FiX,
  FiSettings,
  FiShield,
  FiPackage,
  FiUserCheck,
  FiPieChart,
  FiGrid,
} from "react-icons/fi";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "../../components/languageSwitcher/languageSwitcher";

import Users from "../../components/users/users";
import Orders from "../../components/orders/orders";
import LiveAuctionRequests from "../../components/liveAuctionRequests/liveAuctionRequests";
import OpenAuctionRequests from "../../components/openAuctionRequests/openAuctionRequests";
import Winnings from "../../components/winnings/winnings";
import Admins from "../../components/admins/admins";
import Categories from "../../components/categories/categories";
import ConsignmentApplications from "../../components/consignmentApplications/consignmentApplications";
import ExpertApplications from "../../components/expertApplications/expertApplications";
import Statistics from "../../components/statistics/statistics";
import MyProfile from "../../components/myProfile/myProfile";
import DashBoardLogin from "../../components/dashboardLogin/dashboardLogin";
import adminAxios, { ADMIN_SESSION_EXPIRED_EVENT } from "../../utils/adminAxios";
import { getAdminAuth, clearAdminAuth } from "../../utils/adminAuth";

const Dashboard = () => {
  const { t } = useTranslation();
  const [isLogin, setIsLogin] = useState(() => Boolean(getAdminAuth()?.accessToken));
  const [activeTab, setActiveTab] = useState("statistics");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [admin, setAdmin] = useState(() => getAdminAuth()?.admin || null);
  const [profileModalOpen, setProfileModalOpen] = useState(false);

  useEffect(() => {
    const handleSessionExpired = () => {
      setIsLogin(false);
      setAdmin(null);
      toast.error(t("dashboard.shell.sessionExpired"));
    };
    window.addEventListener(ADMIN_SESSION_EXPIRED_EVENT, handleSessionExpired);
    return () =>
      window.removeEventListener(ADMIN_SESSION_EXPIRED_EVENT, handleSessionExpired);
  }, [t]);

  const handleLoginSuccess = (adminData) => {
    setAdmin(adminData);
    setIsLogin(true);
  };

  const navigation = [
    {
      id: "statistics",
      title: t("dashboard.shell.nav.statistics"),
      icon: <FiPieChart className="w-5 h-5" />,
      color: "text-blue-600",
      bgColor: "bg-blue-50 dark:bg-blue-900/20",
    },
    {
      id: "users",
      title: t("dashboard.shell.nav.users"),
      icon: <FiUsers className="w-5 h-5" />,
      color: "text-blue-500",
      bgColor: "bg-blue-50 dark:bg-blue-900/20",
    },
    {
      id: "orders",
      title: t("dashboard.shell.nav.orders"),
      icon: <FiShoppingCart className="w-5 h-5" />,
      color: "text-green-500",
      bgColor: "bg-green-50 dark:bg-green-900/20",
    },
    // {
    //   id: "requests",
    //   title: t("dashboard.shell.nav.requests"),
    //   icon: <FiCheckSquare className="w-5 h-5" />,
    //   color: "text-purple-500",
    //   bgColor: "bg-purple-50 dark:bg-purple-900/20",
    // },
    {
      id: "openAuctionRequests",
      title: t("dashboard.shell.nav.openAuctionRequests"),
      icon: <FiCheckSquare className="w-5 h-5" />,
      color: "text-purple-500",
      bgColor: "bg-purple-50 dark:bg-purple-900/20",
    },
    {
      id: "winnings",
      title: t("dashboard.shell.nav.winnings"),
      icon: <FiAward className="w-5 h-5" />,
      color: "text-yellow-500",
      bgColor: "bg-yellow-50 dark:bg-yellow-900/20",
    },
    {
      id: "consignments",
      title: t("dashboard.shell.nav.consignments"),
      icon: <FiPackage className="w-5 h-5" />,
      color: "text-teal-500",
      bgColor: "bg-teal-50 dark:bg-teal-900/20",
    },
    {
      id: "expertApplications",
      title: t("dashboard.shell.nav.expertApplications"),
      icon: <FiUserCheck className="w-5 h-5" />,
      color: "text-orange-500",
      bgColor: "bg-orange-50 dark:bg-orange-900/20",
    },
    {
      id: "reports",
      title: t("dashboard.shell.nav.reports"),
      icon: <FiBarChart className="w-5 h-5" />,
      color: "text-indigo-500",
      bgColor: "bg-indigo-50 dark:bg-indigo-900/20",
    },
    {
      id: "categories",
      title: t("dashboard.shell.nav.categories"),
      icon: <FiGrid className="w-5 h-5" />,
      color: "text-pink-500",
      bgColor: "bg-pink-50 dark:bg-pink-900/20",
    },
    {
      id: "admins",
      title: t("dashboard.shell.nav.admins"),
      icon: <FiShield className="w-5 h-5" />,
      color: "text-red-500",
      bgColor: "bg-red-50 dark:bg-red-900/20",
    },
  ];

  const handleLogout = async () => {
    const auth = getAdminAuth();
    try {
      if (auth?.refreshToken) {
        await adminAxios.post("auth/logout", {
          refresh_token: auth.refreshToken,
        });
      }
    } catch {
      // ignore — we clear local session regardless
    } finally {
      clearAdminAuth();
      setAdmin(null);
      setIsLogin(false);
      toast.success(t("dashboard.shell.logoutSuccess"));
    }
  };

  const renderContent = () => {
    switch (activeTab) {
      case "statistics":
        return <Statistics />;
      case "users":
        return <Users />;
      case "orders":
        return <Orders />;
      case "requests":
        return <LiveAuctionRequests />;
      case "openAuctionRequests":
        return <OpenAuctionRequests />;
      case "winnings":
        return <Winnings />;
      case "consignments":
        return <ConsignmentApplications />;
      case "expertApplications":
        return <ExpertApplications />;
      case "categories":
        return <Categories />;
      case "admins":
        return <Admins />;
      case "reports":
        return <div className="p-6">{t("dashboard.shell.reportsComingSoon")}</div>;
      default:
        return <Statistics />;
    }
  };

  if (!isLogin) {
    return (
      <>
        <DashBoardLogin onLogin={handleLoginSuccess} />
        {/* <ToastContainer position="top-right" /> */}
      </>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-900">
      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 rtl:left-auto rtl:right-0 z-50 w-64 bg-white dark:bg-gray-800 shadow-xl lg:static lg:inset-0 transition-transform duration-300 ease-in-out ${
          sidebarOpen
            ? "translate-x-0"
            : "-translate-x-full max-lg:rtl:translate-x-full lg:translate-x-0"
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-3 rtl:space-x-reverse">
              <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg"></div>
              <span className="text-xl font-bold text-gray-800 dark:text-white">
                {t("dashboard.shell.title")}
              </span>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <FiX className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-2">
            {navigation.map((item) => (
              <button
                key={item.id}
                onClick={() => {
                  setActiveTab(item.id);
                  if (window.innerWidth < 1024) {
                    setSidebarOpen(false);
                  }
                }}
                className={`w-full flex items-center space-x-3 rtl:space-x-reverse p-3 rounded-xl transition-all duration-200 hover:scale-105 active:scale-95 ${
                  activeTab === item.id
                    ? `${item.bgColor} ${item.color} shadow-md`
                    : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                }`}
              >
                {item.icon}
                <span className="font-medium">{item.title}</span>
              </button>
            ))}
          </nav>

          {/* User Section */}
          <div className="p-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-3 rtl:space-x-reverse p-3">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                <span className="text-white font-semibold">
                  {(admin?.adminName || "A").charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                  {admin?.adminName || t("dashboard.shell.defaultAdminName")}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                  {t("dashboard.shell.administrator")}
                </p>
              </div>
              <button
                onClick={handleLogout}
                className="p-2 text-gray-500 hover:text-red-500 transition-colors"
              >
                <FiLogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden transition-opacity duration-300"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Bar */}
        <header className="bg-white dark:bg-gray-800 shadow-sm z-10">
          <div className="flex items-center justify-between p-4">
            <div className="flex items-center space-x-4 rtl:space-x-reverse">
              <button
                onClick={() => setSidebarOpen(true)}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors lg:hidden"
              >
                <FiMenu className="w-5 h-5" />
              </button>
              <h1 className="text-2xl font-bold text-gray-800 dark:text-white">
                {navigation.find((item) => item.id === activeTab)?.title}
              </h1>
            </div>
            <div className="flex items-center space-x-4 rtl:space-x-reverse">
              <LanguageSwitcher />
              <button
                onClick={() => setProfileModalOpen(true)}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <FiSettings className="w-5 h-5" />
              </button>
            </div>
          </div>
        </header>

        {/* Main Content Area */}
        <main
          className="flex-1 overflow-auto p-6">
          <div
            key={activeTab}
            className="h-full transition-all duration-300 opacity-100"
          >
            {renderContent()}
          </div>
        </main>
      </div>

      <MyProfile
        open={profileModalOpen}
        onClose={() => setProfileModalOpen(false)}
        admin={admin}
        onUpdated={(updatedAdmin) => setAdmin(updatedAdmin)}
      />

      {/* <ToastContainer position="top-right" /> */}
    </div>
  );
};

export default Dashboard;
